import { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import {
  Inbox, Mail, MailOpen, Archive, Trash2, Forward,
  Loader2, RefreshCw, AlertTriangle, ChevronLeft
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import adminApi from '../../services/adminApi';

import { PageTitle } from './shared';
import { useRememberedTab } from './navigation/useRememberedTab';

const PER_PAGE = 20;
const INBOX_TABS = ['all', 'unread', 'archived'];

const formatRelativeDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } else if (diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
};

const formatFullDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const toast = useCallback(({ title, description, variant = 'default' }) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, title, description, variant }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  return { toasts, toast };
};

const ToastContainer = ({ toasts }) => {
  if (!toasts.length) return null;
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`rounded-lg px-4 py-3 shadow-lg text-sm max-w-sm ${
            t.variant === 'destructive'
              ? 'bg-red-600 text-white'
              : 'bg-stone-800 text-white dark:bg-stone-100 dark:text-stone-900'
          }`}
        >
          {t.title && <div className="font-semibold">{t.title}</div>}
          {t.description && <div className="opacity-90">{t.description}</div>}
        </div>
      ))}
    </div>
  );
};

// ─── List View ───────────────────────────────────────────────────────────────

function InboxList({ onSelectEmail }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useRememberedTab('inbox.status-tab', INBOX_TABS, 'all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const { toasts, toast } = useToast();

  const fetchEmails = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), per_page: PER_PAGE.toString() });
      if (tab !== 'all') params.append('status', tab);
      const response = await adminApi.get(
        `/api/admin/inbox?${params.toString()}`
      );
      if (response.ok) {
        const data = await response.json();
        setEmails(data.emails ?? data.items ?? data ?? []);
        setTotalPages(data.total_pages ?? data.pages ?? 1);
        setTotalCount(data.total ?? data.count ?? 0);
      } else {
        toast({ title: 'Failed to load inbox', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error loading inbox', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [tab, page, toast]);

  useEffect(() => {
    setPage(1);
  }, [tab]);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  const TABS = [
    { key: 'all', label: 'All' },
    { key: 'unread', label: 'Unread' },
    { key: 'archived', label: 'Archived' },
  ];

  return (
    <div className="space-y-4">
      <ToastContainer toasts={toasts} />

      <PageTitle
        title="Inbox"
        description="Incoming emails to info@owazamartialarts.com"
        icon={<Inbox className="w-6 h-6" />}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={fetchEmails}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      {/* Filter Tabs */}
      <div className="flex items-center gap-1 border-b border-stone-200 dark:border-stone-700">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors admin-touch:min-h-[44px] ${
              tab === key
                ? 'border-amber-600 text-amber-700 dark:text-amber-400'
                : 'border-transparent text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200'
            }`}
          >
            {label}
          </button>
        ))}
        <span className="ml-auto pr-2 text-xs text-stone-500 dark:text-stone-400">
          {totalCount} {totalCount === 1 ? 'email' : 'emails'}
        </span>
      </div>

      {/* Email List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
        </div>
      ) : emails.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-stone-400 dark:text-stone-500 gap-3">
          <Inbox className="w-12 h-12" />
          <p className="text-sm">No emails to show</p>
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y divide-stone-100 dark:divide-stone-800">
              {emails.map((email) => {
                const isUnread = email.status === 'unread';
                const isSpam = !!email.spam_flag;
                const isForwarded = email.forwarded ?? false;

                return (
                  <li
                    key={email.id ?? email._id}
                    onClick={() => onSelectEmail(email.id ?? email._id)}
                    className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors ${
                      isSpam ? 'opacity-60' : ''
                    }`}
                  >
                    {/* Read indicator icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      {isUnread ? (
                        <Mail className="w-4 h-4 text-amber-600" />
                      ) : (
                        <MailOpen className="w-4 h-4 text-stone-400" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className={`text-sm truncate ${
                            isUnread
                              ? 'font-bold text-stone-900 dark:text-stone-100'
                              : 'font-medium text-stone-700 dark:text-stone-300'
                          }`}
                        >
                          {email.sender_name ?? email.from_name ?? email.sender ?? 'Unknown'}
                        </span>
                        <span className="text-xs text-stone-400 dark:text-stone-500 flex-shrink-0">
                          {formatRelativeDate(email.received_at ?? email.created_at ?? email.date)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-sm text-stone-600 dark:text-stone-400 truncate">
                          {email.subject ?? '(no subject)'}
                        </span>
                        {isSpam && (
                          <Badge
                            variant="outline"
                            className="text-amber-700 border-amber-300 bg-amber-50 dark:text-amber-400 dark:border-amber-700 dark:bg-amber-900/20 text-xs flex-shrink-0"
                          >
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Likely spam
                          </Badge>
                        )}
                        {isForwarded && (
                          <Badge
                            variant="outline"
                            className="text-green-700 border-green-300 bg-green-50 dark:text-green-400 dark:border-green-700 dark:bg-green-900/20 text-xs flex-shrink-0"
                          >
                            <Forward className="w-3 h-3 mr-1" />
                            Forwarded
                          </Badge>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            Previous
          </Button>
          <span className="text-sm text-stone-500 dark:text-stone-400">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Detail View ──────────────────────────────────────────────────────────────

function EmailDetail({ emailId, onBack }) {
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { toasts, toast } = useToast();

  useEffect(() => {
    const fetchEmail = async () => {
      setLoading(true);
      try {
        const response = await adminApi.get(`/api/admin/inbox/${emailId}`);
        if (response.ok) {
          const data = await response.json();
          setEmail(data);
        } else {
          toast({ title: 'Failed to load email', variant: 'destructive' });
        }
      } catch {
        toast({ title: 'Error loading email', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    fetchEmail();
  }, [emailId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleForward = async () => {
    setActionLoading('forward');
    try {
      const response = await adminApi.post(`/api/admin/inbox/${emailId}/forward`);
      if (response.ok) {
        toast({ title: 'Email forwarded to admins' });
        setEmail((prev) => prev ? { ...prev, forwarded: true } : prev);
      } else {
        toast({ title: 'Failed to forward email', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error forwarding email', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleArchive = async () => {
    setActionLoading('archive');
    try {
      const response = await adminApi.put(`/api/admin/inbox/${emailId}/status?status=archived`);
      if (response.ok) {
        toast({ title: 'Email archived' });
        onBack();
      } else {
        toast({ title: 'Failed to archive email', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error archiving email', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    setActionLoading('delete');
    try {
      const response = await adminApi.delete(`/api/admin/inbox/${emailId}`);
      if (response.ok) {
        toast({ title: 'Email deleted' });
        onBack();
      } else {
        toast({ title: 'Failed to delete email', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error deleting email', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const isSpam = !!email?.spam_flag;
  const isForwarded = email?.forwarded ?? false;

  return (
    <div className="space-y-4">
      <ToastContainer toasts={toasts} />

      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
        </div>
      ) : !email ? (
        <div className="flex flex-col items-center justify-center py-16 text-stone-400 gap-3">
          <Mail className="w-10 h-10" />
          <p className="text-sm">Email not found</p>
        </div>
      ) : (
        <Card>
          <CardContent className="p-6 space-y-4">
            {/* Subject */}
            <h2 className="text-xl font-bold text-stone-800 dark:text-stone-100">
              {email.subject ?? '(no subject)'}
            </h2>

            {/* Meta */}
            <div className="space-y-1 text-sm text-stone-600 dark:text-stone-400">
              <div>
                <span className="font-medium">From:</span>{' '}
                {email.sender_name ?? email.from_name ?? 'Unknown'}
                {(email.sender_email ?? email.from_email ?? email.sender) && (
                  <span className="ml-1 text-stone-400">
                    &lt;{email.sender_email ?? email.from_email ?? email.sender}&gt;
                  </span>
                )}
              </div>
              <div>
                <span className="font-medium">Received:</span>{' '}
                {formatFullDate(email.received_at ?? email.created_at ?? email.date)}
              </div>
            </div>

            {/* Badges */}
            {(isSpam || isForwarded) && (
              <div className="flex items-center gap-2">
                {isSpam && (
                  <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-amber-200 dark:border-amber-700">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Likely spam
                  </Badge>
                )}
                {isForwarded && (
                  <Badge className="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 border-green-200 dark:border-green-700">
                    <Forward className="w-3 h-3 mr-1" />
                    Forwarded
                  </Badge>
                )}
              </div>
            )}

            {/* Divider */}
            <hr className="border-stone-200 dark:border-stone-700" />

            {/* Body */}
            <div className="text-sm text-stone-700 dark:text-stone-300">
              {email.body_html ? (
                <div
                  className="prose prose-sm dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(email.body_html) }}
                />
              ) : (
                <pre className="whitespace-pre-wrap font-sans">
                  {email.body_text ?? email.body ?? email.text ?? '(no content)'}
                </pre>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleForward}
                disabled={!!actionLoading || isForwarded}
              >
                {actionLoading === 'forward' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Forward className="w-4 h-4 mr-2" />
                )}
                Forward to Admins
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={handleArchive}
                disabled={!!actionLoading}
              >
                {actionLoading === 'archive' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Archive className="w-4 h-4 mr-2" />
                )}
                Archive
              </Button>

              <Button
                variant="outline"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 border-red-200 dark:border-red-800"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={!!actionLoading}
              >
                {actionLoading === 'delete' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Delete
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Email</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the email. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function InboxManager() {
  const [selectedEmailId, setSelectedEmailId] = useState(null);

  if (selectedEmailId) {
    return (
      <EmailDetail
        emailId={selectedEmailId}
        onBack={() => setSelectedEmailId(null)}
      />
    );
  }

  return <InboxList onSelectEmail={setSelectedEmailId} />;
}
